import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Card } from "../ui/card";
import { getWeekEvents, listEvents, getEventTypes, getHolidays } from "../../lib/api";
import {
  Calendar,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Users,
  Building,
  GraduationCap,
  Bus,
  UserCircle,
  Waves,
  BookOpen,
  ClipboardCheck,
  AlertCircle,
  Presentation,
  Palmtree,
  Flag,
} from "lucide-react";
import { format, startOfMonth, endOfMonth, eachDayOfInterval, addMonths, subMonths, isSameMonth, isToday } from "date-fns";
import { fr } from "date-fns/locale";

const ICON_MAP = {
  Users: Users,
  Building: Building,
  GraduationCap: GraduationCap,
  Bus: Bus,
  UserCircle: UserCircle,
  Waves: Waves,
  BookOpen: BookOpen,
  ClipboardCheck: ClipboardCheck,
  AlertCircle: AlertCircle,
  Presentation: Presentation,
  Calendar: Calendar,
};

const STATUS_COLORS = {
  planifie: "bg-blue-500",
  confirme: "bg-green-500",
  annule: "bg-red-500",
  termine: "bg-slate-400",
};

function MonthGrid({ currentMonth, events, eventTypes, holidays, onDayClick }) {
  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const days = eachDayOfInterval({ start: monthStart, end: monthEnd });
  
  // Get the day of week for the first day (0 = Sunday, 1 = Monday, etc.)
  const startDayOfWeek = monthStart.getDay();
  // Adjust for Monday start (0 = Monday, 6 = Sunday)
  const adjustedStart = startDayOfWeek === 0 ? 6 : startDayOfWeek - 1;
  
  // Group events by date
  const eventsByDate = events.reduce((acc, event) => {
    if (!acc[event.date]) acc[event.date] = [];
    acc[event.date].push(event);
    return acc;
  }, {});
  
  // Check if a date is a holiday or vacation
  const getHolidayInfo = (dateStr) => {
    // Check public holidays
    const publicHoliday = holidays?.public_holidays?.find(h => h.date === dateStr);
    if (publicHoliday) {
      return { type: "ferie", name: publicHoliday.name };
    }
    
    // Check vacations
    const dateObj = new Date(dateStr);
    for (const vac of holidays?.vacations || []) {
      const start = new Date(vac.start);
      const end = new Date(vac.end);
      if (dateObj >= start && dateObj <= end) {
        return { type: "vacances", name: vac.name };
      }
    }
    
    return null;
  };
  
  const weekdays = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];
  
  return (
    <div className="grid grid-cols-7 gap-1">
      {/* Weekday headers */}
      {weekdays.map(day => (
        <div key={day} className="text-center text-xs font-medium text-slate-500 py-2">
          {day}
        </div>
      ))}
      
      {/* Empty cells before month start */}
      {Array.from({ length: adjustedStart }).map((_, i) => (
        <div key={`empty-${i}`} className="h-20 bg-slate-50 rounded" />
      ))}
      
      {/* Days of the month */}
      {days.map(day => {
        const dateStr = format(day, "yyyy-MM-dd");
        const dayEvents = eventsByDate[dateStr] || [];
        const hasEvents = dayEvents.length > 0;
        const today = isToday(day);
        const holidayInfo = getHolidayInfo(dateStr);
        const isWeekend = day.getDay() === 0 || day.getDay() === 6;
        
        return (
          <div
            key={dateStr}
            onClick={() => onDayClick(day, dayEvents, holidayInfo)}
            className={`
              h-24 p-1 rounded border cursor-pointer transition-all overflow-hidden
              ${today ? "border-indigo-500 ring-2 ring-indigo-200" : "border-slate-200"}
              ${holidayInfo?.type === "ferie" ? "bg-gradient-to-br from-red-50 to-orange-50 border-red-200" : ""}
              ${holidayInfo?.type === "vacances" ? "bg-gradient-to-br from-amber-50 to-yellow-50 border-amber-200" : ""}
              ${isWeekend && !holidayInfo ? "bg-slate-50" : ""}
              ${!holidayInfo && !isWeekend ? "hover:border-indigo-300 hover:bg-slate-50" : ""}
            `}
          >
            <div className={`text-xs font-medium flex items-center justify-between ${today ? "text-indigo-600" : holidayInfo?.type === "ferie" ? "text-red-600" : holidayInfo?.type === "vacances" ? "text-amber-600" : "text-slate-600"}`}>
              <span>{format(day, "d")}</span>
              {holidayInfo?.type === "ferie" && <Flag className="w-3 h-3 text-red-500" />}
              {holidayInfo?.type === "vacances" && <Palmtree className="w-3 h-3 text-amber-500" />}
            </div>
            
            {/* Holiday/vacation name */}
            {holidayInfo && (
              <div className={`text-[9px] font-medium mt-0.5 truncate ${holidayInfo.type === "ferie" ? "text-red-600" : "text-amber-600"}`}>
                {holidayInfo.name}
              </div>
            )}
            
            {/* Events */}
            {dayEvents.slice(0, holidayInfo ? 2 : 3).map((event, idx) => {
              const eventType = eventTypes.find(t => t.id === event.event_type);
              const Icon = ICON_MAP[eventType?.icon] || Calendar;
              const statusColor = STATUS_COLORS[event.status] || STATUS_COLORS.planifie;
              
              return (
                <div
                  key={event.id}
                  className="flex items-center gap-1 mt-0.5 px-1 py-0.5 rounded text-[9px] bg-white border truncate"
                  style={{ borderLeftColor: eventType?.color || "#64748B", borderLeftWidth: 2 }}
                >
                  <span className={`w-1.5 h-1.5 rounded-full ${statusColor}`} />
                  <span className="truncate">{event.title}</span>
                </div>
              );
            })}
            
            {dayEvents.length > (holidayInfo ? 2 : 3) && (
              <div className="text-[9px] text-slate-500 mt-0.5 text-center">
                +{dayEvents.length - (holidayInfo ? 2 : 3)} autres
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function DayEventsPanel({ selectedDay, events, eventTypes, holidayInfo, onClose }) {
  if (!selectedDay) return null;
  
  return (
    <Card className="mt-4 p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-medium">
          {format(selectedDay, "EEEE d MMMM yyyy", { locale: fr })}
        </h3>
        <Button variant="ghost" size="sm" onClick={onClose}>
          Fermer
        </Button>
      </div>
      
      {/* Holiday/vacation info */}
      {holidayInfo && (
        <div className={`mb-3 p-2 rounded-lg ${holidayInfo.type === "ferie" ? "bg-red-50 border border-red-200" : "bg-amber-50 border border-amber-200"}`}>
          <div className="flex items-center gap-2">
            {holidayInfo.type === "ferie" ? (
              <Flag className="w-4 h-4 text-red-500" />
            ) : (
              <Palmtree className="w-4 h-4 text-amber-500" />
            )}
            <span className={`font-medium text-sm ${holidayInfo.type === "ferie" ? "text-red-700" : "text-amber-700"}`}>
              {holidayInfo.name}
            </span>
          </div>
          <p className="text-xs mt-1 text-slate-600">
            {holidayInfo.type === "ferie" ? "Jour férié - Pas d'école" : "Vacances scolaires"}
          </p>
        </div>
      )}
      
      {events.length === 0 && !holidayInfo ? (
        <p className="text-sm text-slate-500">Aucun événement ce jour</p>
      ) : events.length === 0 ? null : (
        <div className="space-y-2">
          {events.map(event => {
            const eventType = eventTypes.find(t => t.id === event.event_type);
            const Icon = ICON_MAP[eventType?.icon] || Calendar;
            
            return (
              <div
                key={event.id}
                className="flex items-start gap-3 p-2 rounded border"
                style={{ borderLeftColor: eventType?.color || "#64748B", borderLeftWidth: 3 }}
              >
                <div
                  className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: `${eventType?.color}20` }}
                >
                  <Icon className="w-4 h-4" style={{ color: eventType?.color }} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm">{event.title}</div>
                  <div className="text-xs text-slate-500">
                    {event.start_time && event.end_time 
                      ? `${event.start_time} - ${event.end_time}`
                      : event.all_day 
                        ? "Toute la journée" 
                        : ""
                    }
                    {event.location && ` • ${event.location}`}
                  </div>
                  {event.description && (
                    <p className="text-xs text-slate-600 mt-1">{event.description}</p>
                  )}
                </div>
                <Badge variant="secondary" className="text-[10px]">
                  {eventType?.label || "Autre"}
                </Badge>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}

export default function MonthlyCalendarDialog({ open, onOpenChange }) {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [eventTypes, setEventTypes] = useState([]);
  const [holidays, setHolidays] = useState({ vacations: [], public_holidays: [] });
  const [loading, setLoading] = useState(true);
  const [selectedDay, setSelectedDay] = useState(null);
  const [selectedDayEvents, setSelectedDayEvents] = useState([]);
  const [selectedHolidayInfo, setSelectedHolidayInfo] = useState(null);
  
  // Fetch event types and holidays once
  useEffect(() => {
    getEventTypes().then(res => setEventTypes(res.data)).catch(console.error);
    getHolidays().then(res => setHolidays(res.data)).catch(console.error);
  }, []);
  
  // Fetch events for current month
  useEffect(() => {
    if (!open) return;
    fetchMonthEvents();
  }, [open, currentMonth]);
  
  const fetchMonthEvents = async () => {
    setLoading(true);
    try {
      const startDate = format(startOfMonth(currentMonth), "yyyy-MM-dd");
      const endDate = format(endOfMonth(currentMonth), "yyyy-MM-dd");
      
      const res = await listEvents({ start_date: startDate, end_date: endDate, limit: 200 });
      setEvents(res.data || []);
    } catch (err) {
      console.error("Error fetching events:", err);
    } finally {
      setLoading(false);
    }
  };
  
  const handlePrevMonth = () => {
    setCurrentMonth(subMonths(currentMonth, 1));
    setSelectedDay(null);
  };
  
  const handleNextMonth = () => {
    setCurrentMonth(addMonths(currentMonth, 1));
    setSelectedDay(null);
  };
  
  const handleDayClick = (day, dayEvents, holidayInfo) => {
    setSelectedDay(day);
    setSelectedDayEvents(dayEvents);
    setSelectedHolidayInfo(holidayInfo);
  };
  
  // Count events by type for summary
  const eventCountByType = events.reduce((acc, event) => {
    acc[event.event_type] = (acc[event.event_type] || 0) + 1;
    return acc;
  }, {});
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-indigo-600" />
              Calendrier mensuel
            </span>
          </DialogTitle>
        </DialogHeader>
        
        {/* Month navigation */}
        <div className="flex items-center justify-between mb-4">
          <Button variant="outline" size="sm" onClick={handlePrevMonth}>
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <h2 className="text-lg font-semibold capitalize">
            {format(currentMonth, "MMMM yyyy", { locale: fr })}
          </h2>
          <Button variant="outline" size="sm" onClick={handleNextMonth}>
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
        
        {/* Event type summary */}
        {events.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-4">
            {Object.entries(eventCountByType).map(([typeId, count]) => {
              const eventType = eventTypes.find(t => t.id === typeId);
              if (!eventType) return null;
              return (
                <Badge
                  key={typeId}
                  variant="secondary"
                  className="text-xs"
                  style={{ borderColor: eventType.color, borderWidth: 1 }}
                >
                  {eventType.label}: {count}
                </Badge>
              );
            })}
          </div>
        )}
        
        {/* Calendar grid */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
          </div>
        ) : (
          <MonthGrid
            currentMonth={currentMonth}
            events={events}
            eventTypes={eventTypes}
            holidays={holidays}
            onDayClick={handleDayClick}
          />
        )}
        
        {/* Holiday legend */}
        <div className="flex flex-wrap gap-3 mt-4 pt-4 border-t">
          <div className="flex items-center gap-1.5 text-xs">
            <Flag className="w-3 h-3 text-red-500" />
            <span className="text-slate-600">Jour férié</span>
          </div>
          <div className="flex items-center gap-1.5 text-xs">
            <Palmtree className="w-3 h-3 text-amber-500" />
            <span className="text-slate-600">Vacances scolaires</span>
          </div>
        </div>
        
        {/* Selected day details */}
        <DayEventsPanel
          selectedDay={selectedDay}
          events={selectedDayEvents}
          eventTypes={eventTypes}
          holidayInfo={selectedHolidayInfo}
          onClose={() => setSelectedDay(null)}
        />
        
        {/* Empty state */}
        {!loading && events.length === 0 && (
          <div className="text-center py-8 text-slate-500">
            <Calendar className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>Aucun événement ce mois-ci</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
